/*
  Carrito lateral de Dolly Café.

  Todo cuelga de listeners delegados en `document`: el contenido del panel se
  reemplaza en cada mutación con la Section Rendering API, así que enlazar
  botón por botón obligaría a re-enlazar tras cada respuesta (y tras
  shopify:section:load en el editor de temas).

  Los totales nunca se calculan aquí: cada mutación se responde volviendo a
  pedirle a Shopify el HTML de la sección, que ya trae precios y moneda.
*/
(() => {
  const SELECTOR_FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const SECCIONES = 'cart-drawer,header';

  const getDrawer = () => document.querySelector('[data-cart-drawer]');
  const isOpen = (drawer) => drawer.getAttribute('aria-hidden') === 'false';
  const raiz = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let lastTrigger = null;

  /* ---------------------------------------------------------------- panel */

  const openDrawer = (trigger) => {
    const drawer = getDrawer();
    if (!drawer) return;
    if (trigger) lastTrigger = trigger;
    if (isOpen(drawer)) {
      drawer.querySelector('.cart-drawer__heading')?.focus();
      return;
    }
    if (!lastTrigger) lastTrigger = document.activeElement;
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    drawer.querySelector('.cart-drawer__heading')?.focus();
  };

  const closeDrawer = () => {
    const drawer = getDrawer();
    if (!drawer || !isOpen(drawer)) return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    document.body.classList.remove('cart-drawer-open');
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
    lastTrigger = null;
  };

  const anunciar = (mensaje) => {
    const status = document.querySelector('[data-drawer-status]');
    if (status) status.textContent = mensaje;
  };

  /* ------------------------------------------------- render de secciones */

  const pedirSecciones = async () => {
    const respuesta = await fetch(`${raiz()}?sections=${SECCIONES}`, {
      headers: { Accept: 'application/json' },
    });
    if (!respuesta.ok) throw new Error('No se pudieron pedir las secciones');
    return respuesta.json();
  };

  const aplicarSecciones = (datos) => {
    const parser = new DOMParser();

    if (datos['cart-drawer']) {
      const nuevo = parser
        .parseFromString(datos['cart-drawer'], 'text/html')
        .querySelector('[data-drawer-content]');
      const actual = document.querySelector('[data-drawer-content]');
      if (nuevo && actual) actual.innerHTML = nuevo.innerHTML;
    }

    if (datos.header) {
      const nuevo = parser
        .parseFromString(datos.header, 'text/html')
        .querySelector('.site-header__cart');
      const actual = document.querySelector('.site-header__cart');
      // Se reemplaza el interior, no el nodo: así sigue siendo válida la
      // referencia guardada en lastTrigger para devolverle el foco.
      if (nuevo && actual) {
        actual.innerHTML = nuevo.innerHTML;
        const etiqueta = nuevo.getAttribute('aria-label');
        if (etiqueta) actual.setAttribute('aria-label', etiqueta);
      }
    }
  };

  const refrescar = async () => {
    aplicarSecciones(await pedirSecciones());
  };

  /* ------------------------------------------------------ botón y errores */

  const cargando = (boton, activo) => {
    if (!boton) return;
    if (activo) {
      boton.dataset.textoPrevio = boton.textContent.trim();
      if (boton.dataset.loadingText) boton.textContent = boton.dataset.loadingText;
      boton.setAttribute('aria-busy', 'true');
      boton.disabled = true;
      return;
    }
    if (boton.dataset.textoPrevio) boton.textContent = boton.dataset.textoPrevio;
    delete boton.dataset.textoPrevio;
    boton.removeAttribute('aria-busy');
    boton.disabled = false;
  };

  const mostrarError = (mensaje) => {
    if (window.DollySnackbar) {
      window.DollySnackbar.show(mensaje, 'error');
    }
  };

  /* ------------------------------------------------- editar dentro del panel */

  // Los cambios se encolan: pulsar +/− varias veces seguidas dispara peticiones
  // que Shopify resuelve sobre el mismo carrito, y en paralelo se pisarían.
  let cola = Promise.resolve();
  const encolar = (tarea) => {
    cola = cola.then(tarea, tarea);
    return cola;
  };

  const devolverFoco = (linea, paso) => {
    const drawer = getDrawer();
    if (!drawer || !isOpen(drawer)) return;
    const destino = drawer.querySelector(`[data-drawer-line="${linea}"] [data-step="${paso}"]`);
    // Si la línea desapareció (se eliminó o era la última), el foco vuelve al
    // encabezado en vez de perderse en el body.
    (destino || drawer.querySelector('.cart-drawer__heading'))?.focus();
  };

  const cambiarLinea = async (linea, cantidad, item, paso) => {
    try {
      const respuesta = await fetch(`${raiz()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: linea, quantity: cantidad }),
      });
      const datos = await respuesta.json();

      if (!respuesta.ok) {
        item?.removeAttribute('data-drawer-busy');
        const mensaje = datos.description || datos.message || 'No pudimos actualizar el carrito.';
        anunciar(mensaje);
        mostrarError(mensaje);
        return;
      }

      const resultante = datos.items[linea - 1];
      await refrescar();

      if (cantidad === 0) {
        anunciar('Producto eliminado del carrito');
      } else if (resultante && resultante.quantity !== cantidad) {
        // Shopify recorta la cantidad cuando no alcanza el inventario.
        anunciar(`Solo quedan ${resultante.quantity} disponibles`);
      } else {
        anunciar('Cantidad actualizada');
      }

      devolverFoco(linea, paso);
    } catch (error) {
      item?.removeAttribute('data-drawer-busy');
      anunciar('Se nos cayó la conexión. Inténtalo otra vez, amiwita.');
    }
  };

  /* -------------------------------------------------------------- agregar */

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('.dolly-product-form');
    if (!form) return;

    const boton = form.querySelector('[data-dolly-submit]');
    if (boton && boton.disabled) return;

    // Sin panel montado (o sin fetch) se deja pasar el envío nativo a /cart,
    // que es el respaldo sin JS.
    if (!getDrawer() || typeof window.fetch !== 'function') {
      cargando(boton, true);
      return;
    }

    event.preventDefault();
    cargando(boton, true);

    try {
      const respuesta = await fetch(`${raiz()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const datos = await respuesta.json();

      if (!respuesta.ok) {
        mostrarError(datos.description || datos.message || 'No pudimos agregarlo, amiwita.');
        return;
      }

      await refrescar();
      anunciar('Producto agregado al carrito');
      openDrawer(boton);
    } catch (error) {
      mostrarError('Se nos cayó la conexión. Inténtalo otra vez, amiwita.');
    } finally {
      cargando(boton, false);
    }
  });

  /* ------------------------------------------------------------- teclado */

  document.addEventListener('click', (event) => {
    const drawer = getDrawer();
    if (!drawer) return;

    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const control = event.target.closest('[data-drawer-quantity]');
    if (control) {
      event.preventDefault();
      const item = control.closest('.cart-drawer__item');
      // Mientras la línea está en vuelo se ignoran más pulsaciones: el
      // data-quantity del DOM todavía es el viejo y encolarlo pediría dos
      // veces la misma cantidad en lugar de sumar.
      if (item?.hasAttribute('data-drawer-busy')) return;
      item?.setAttribute('data-drawer-busy', '');
      const linea = Number(control.dataset.line);
      const cantidad = Number(control.dataset.quantity);
      const paso = control.dataset.step;
      encolar(() => cambiarLinea(linea, cantidad, item, paso));
      return;
    }

    // El icono del carrito abre el panel; sin JS sigue siendo un enlace a /cart.
    const cartLink = event.target.closest('.site-header__cart');
    if (cartLink) {
      event.preventDefault();
      openDrawer(cartLink);
    }
  });

  document.addEventListener('keydown', (event) => {
    const drawer = getDrawer();
    if (!drawer || !isOpen(drawer)) return;

    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusables = [...drawer.querySelectorAll(SELECTOR_FOCUSABLE)].filter(
      (element) => element.offsetParent !== null || element === document.activeElement
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
